/**
 * DeepAnalysisStatusCard — Phase 8 (Deep Analysis admin)
 *
 * Renders on the System Status page (admin only). Surfaces:
 *   • Counts of analyses (total, valid, this month, total views)
 *   • Macro & Geo cache freshness with [Force refresh] buttons
 *   • Quick link into the full /admin/deep-analysis ledger
 *
 * Data sourced from GET /api/admin/deep-analysis/stats
 * Force-refresh:
 *   POST /api/admin/deep-analysis/refresh-macro-cache
 *   POST /api/admin/deep-analysis/refresh-geo-cache
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  RefreshCcw,
  Eye,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import { toast } from "sonner";

const ageInDays = (iso) => {
  if (!iso) return null;
  const dt = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(dt.getTime())) return null;
  return Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24));
};

const ageBadgeTone = (days) => {
  if (days == null) return "border-border text-muted-foreground";
  if (days <= 1) return "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]";
  if (days <= 7) return "border-border text-foreground";
  return "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400";
};

const CacheRow = ({ label, icon: Icon, cache, schedule, onRefresh, refreshing, testId }) => {
  const days = ageInDays(cache?.generated_at);
  const sectors = cache?.sectors ?? 0;
  const tone = ageBadgeTone(days);
  return (
    <div
      className="flex items-start justify-between gap-3 rounded-md border bg-card p-3"
      data-testid={testId}
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{label}</span>
          {sectors > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              {sectors} sectors
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
          {cache?.generated_at ? (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] ${tone}`}>
              <Clock className="h-3 w-3" />
              {days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} old`}
              <span className="text-muted-foreground/70">
                · {formatDateTime(cache.generated_at)}
              </span>
            </span>
          ) : (
            <span className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> No cache yet
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">{schedule}</div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onRefresh}
        disabled={refreshing}
        data-testid={`${testId}-refresh-btn`}
      >
        {refreshing ? (
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        ) : (
          <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
        )}
        Force refresh
      </Button>
    </div>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-md border bg-card p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="font-display text-xl tabular-nums font-semibold">{value}</div>
  </div>
);

const DeepAnalysisStatusCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState({ macro: false, geo: false });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await apiGet("/admin/deep-analysis/stats");
      setStats(r);
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refreshCache = useCallback(
    async (kind) => {
      setRefreshing((r) => ({ ...r, [kind]: true }));
      try {
        const url =
          kind === "macro"
            ? "/admin/deep-analysis/refresh-macro-cache"
            : "/admin/deep-analysis/refresh-geo-cache";
        const r = await apiPost(url);
        toast.success(
          `${kind === "macro" ? "Macro" : "Geo"} cache refreshed · ${r?.sectors_scored ?? 0} sectors`
        );
        await load();
      } catch (e) {
        toast.error(`Failed to refresh ${kind} cache: ${e?.message || e}`);
      } finally {
        setRefreshing((r) => ({ ...r, [kind]: false }));
      }
    },
    [load]
  );

  if (loading) {
    return (
      <Card data-testid="deep-analysis-status-card">
        <CardHeader>
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Deep Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card data-testid="deep-analysis-status-card">
        <CardHeader>
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Deep Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {error || "Stats unavailable"}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="deep-analysis-status-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Deep Analysis
              <Badge variant="secondary" className="text-[10px]">
                6-specialist research
              </Badge>
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-1">
              Shared community analyses (30-day cache) plus weekly macro/geo refresh.
            </p>
          </div>
          <Link to="/admin/deep-analysis">
            <Button size="sm" variant="outline" data-testid="deep-analysis-view-all-btn">
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              View all analyses
              <ExternalLink className="h-3 w-3 ml-1.5 opacity-70" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat
            label="Stocks analysed"
            value={stats.total_analyses ?? 0}
            testId="da-stat-total"
          />
          <Stat
            label="Valid analyses"
            value={stats.valid_analyses ?? 0}
            testId="da-stat-valid"
          />
          <Stat
            label="This month"
            value={stats.analyses_this_month ?? 0}
            testId="da-stat-month"
          />
          <Stat
            label="Cost this month"
            value={`$${(stats.cost_this_month_usd ?? 0).toFixed(4)}`}
            testId="da-stat-cost"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          <CacheRow
            label="Sri Lanka Macro cache"
            icon={CheckCircle2}
            cache={stats.macro_cache}
            schedule="Auto-refresh: Sundays 07:00 Asia/Colombo"
            onRefresh={() => refreshCache("macro")}
            refreshing={refreshing.macro}
            testId="da-macro-cache"
          />
          <CacheRow
            label="Geopolitical cache"
            icon={CheckCircle2}
            cache={stats.geo_cache}
            schedule="Auto-refresh: Sundays 07:15 Asia/Colombo"
            onRefresh={() => refreshCache("geo")}
            refreshing={refreshing.geo}
            testId="da-geo-cache"
          />
        </div>

        {stats.most_viewed?.length > 0 && (
          <div className="rounded-md border bg-muted/30 p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
              Most viewed
            </div>
            <div className="flex flex-wrap gap-1.5">
              {stats.most_viewed.map((m) => (
                <Badge key={m.symbol} variant="outline" className="font-normal">
                  <span className="font-mono">{m.symbol}</span>
                  <span className="ml-1.5 text-muted-foreground tabular-nums">
                    · {m.view_count} views
                  </span>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeepAnalysisStatusCard;
